import { isNull } from 'lodash';
import { useEffect } from 'react';
import { isIOS } from 'react-device-detect';

import { useAppLoggedInStore } from '../store/appLoggedInStore';
import {
  type GtmEvent,
  type RNEvent,
  RNEventType,
  type RouteEvent,
  type TokenEvent,
} from '../type/react-native';
import {
  NaverConversionType,
  trackNaverConversion,
} from '../utils/common/naver-script';
import { LoginGtm } from '../utils/gtm/auth/loginGtm';
import { SignupGtm } from '../utils/gtm/auth/signupGtm';
import { Gtm } from '../utils/gtm/gtmClass';
import { ReactNativeGtm } from '../utils/gtm/react-native/ReactNativeGtm';
import useHandleGuestMarketCart from '../utils/hook/market-cart/useHandleGuestMarketCart';
import useMeQuery from '../utils/hook/react-query/user/useMeQuery';
import { useHistoryMethod } from '../utils/hook/useHistoryMethod';
import { useReferral } from '../utils/hook/useReferral';
import { useOnLoginSuccess } from '../utils/hook/users';

const ReactNativeListener = () => {
  const { me } = useMeQuery();
  const history = useHistoryMethod();
  const setAppLoggedIn = useAppLoggedInStore.use.setAppLoggedIn();
  const onLoginSuccess = useOnLoginSuccess();
  const { hasReferral } = useReferral();
  const { handleGuestMarketCartMerge } = useHandleGuestMarketCart();

  useEffect(() => {
    if (window?.UglyusApp) {
      new ReactNativeGtm().open_app({
        deep_link: window.UglyusApp.deepLink,
      });
    }
  }, []);

  useEffect(() => {
    if (!('ReactNativeWebView' in window)) return;

    /**
     * 앱에서 로그인시 웹뷰로 토큰을 전송 & 전송받은 토큰으로 웹뷰에서 로그인
     */
    const handleLogin = async (parsed: TokenEvent) => {
      if (!parsed.data) return;

      const { token, onSuccessRedirectURI } = parsed.data;
      const loginMethod = parsed.data?.loginMethod;

      if (isNull(token)) return;

      try {
        const userId = await onLoginSuccess(token);
        setAppLoggedIn(true);

        await handleGuestMarketCartMerge({ token });

        onSuccessRedirectURI && history.replace(onSuccessRedirectURI);

        /**
         * 앱에서 회원가입시
         * @link https://uglyushq.slack.com/archives/C02PNQECLM6/p1731046096238039
         */
        if (parsed.data?.isSignUp) {
          if (loginMethod && userId) {
            new SignupGtm().complete_signup({
              signup_method: loginMethod,
              user_id: userId,
              is_referral: hasReferral(),
            });
            trackNaverConversion({ type: NaverConversionType.SIGN_UP });
          }
        } else {
          loginMethod && new LoginGtm().complete_login(loginMethod);
        }
      } catch (e) {
        console.error('webview login failed');
      }
    };

    /**
     * 앱에서 전송받은 type으로 routing
     */
    const handleRoute = async (parsed: RouteEvent) => {
      history.push(parsed.data);
    };

    /**
     * 앱에서 전송받은 gtm 이벤트 처리
     */
    const handleGtm = async (parsed: GtmEvent) => {
      const { eventName, properties } = parsed.data;

      /**
       * 앱이 열린상태에서 딥링크 처리 되었을 경우
       * @link https://reactnative.dev/docs/0.76/linking#addeventlistener
       */
      if (eventName === 'open_app' && properties && 'deep_link' in properties) {
        return new ReactNativeGtm().open_app({
          deep_link: properties.deep_link,
        });
      }

      new Gtm().track(eventName, properties);
    };

    const listener = (receive: Event) => {
      if (!('data' in receive && typeof receive.data === 'string')) {
        try {
          return console.warn(
            'receive.data is not string',
            JSON.stringify(receive),
          );
        } catch (error) {
          return;
        }
      }

      const tryParseJSON = (jsonString: string): RNEvent | null => {
        try {
          return JSON.parse(jsonString);
        } catch (error) {
          if (jsonString === 'undefined') return null;

          /**
           * 카카오 공유하기를 위한 동작은 에러가 아님
           *@link https://uglyushq.slack.com/archives/C03EPF0HJFJ/p1707367002401979
           */
          if (jsonString.startsWith('default')) return null;

          /**
           * JSON 파싱 에러 로깅
           */
          console.error(`tryParseJSON jsonString:${jsonString}`);
          return null;
        }
      };
      const parsed: RNEvent | null = tryParseJSON(receive.data);

      if (!parsed) {
        try {
          return console.warn(
            'parsed.type is undefined',
            JSON.stringify(parsed),
          );
        } catch (error) {
          return;
        }
      }

      switch (parsed.type) {
        case RNEventType.token:
          handleLogin(parsed);
          break;
        case RNEventType.historyPush:
          handleRoute(parsed);
          break;
        case RNEventType.gtm:
          handleGtm(parsed);
          break;
        case RNEventType.saveImageResult:
          window.dispatchEvent(
            new CustomEvent(RNEventType.saveImageResult, {
              detail: parsed.data,
            }),
          );
          break;
        default:
          break;
      }
    };

    const receiver = isIOS ? window : document;

    receiver.addEventListener('message', listener);

    return () => {
      receiver.removeEventListener('message', listener);
    };
  }, [
    me,
    handleGuestMarketCartMerge,
    hasReferral,
    history,
    onLoginSuccess,
    setAppLoggedIn,
  ]);

  return <></>;
};

export default ReactNativeListener;
